import { fmt } from '../utils/numberFormat';
// PV Anlage specific types for frontend

export interface WeatherData {
  temperature: number;
  cloudCover: number;
  solarIrradiance: number;
  humidity: number;
  windSpeed: number;
  description: string;
  timestamp: string;
  location: {
    latitude: number;
    longitude: number;
  };
}

export interface PvGenerationData {
  currentGenerationKw: number;
  maxPossibleGenerationKw: number;
  efficiencyPercent: number;
  weatherData: WeatherData;
  lastUpdated: string;
  pvSystemId: string;
  hausanschlussId: string;
}

export interface PvGenerationSummary {
  totalCurrentGenerationKw: number;
  totalMaxPossibleGenerationKw: number;
  averageEfficiencyPercent: number;
  activePvSystemCount: number;
  totalInstalledCapacityKw: number;
  pvSystems: PvGenerationData[];
  lastUpdated: string;
}

export interface CreatePvAnlageRequest {
  solarPanelAreaM2: number;
  efficiencyPercent?: number;
  tiltAngleDegrees?: number;
  azimuthDegrees?: number;
  maxPower: number;
  installationDate?: string;
  manufacturer?: string;
  model?: string;
  name?: string;
}

export interface UpdatePvAnlageRequest extends CreatePvAnlageRequest {
  steuerboxId?: string;
  relayNumber?: number;
}

export interface PvConfigurationSuggestions {
  suggestedTiltAngle: number;
  suggestedAzimuthAngle: number;
  expectedAnnualYieldKwhPerKwp: number;
  recommendedEfficiencyRange: {
    min: number;
    max: number;
  };
  recommendations: string[];
}

export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Helper type guards
export const isPvAnlage = (tr: unknown): tr is PvAnlage => {
  return tr !== null && 
         tr !== undefined && 
         typeof tr === 'object' && 
         'type' in tr && 
         (tr as Record<string, unknown>).type === 'pv_anlage';
};

// Extended TechnischeRessource specifically for PV systems
export interface PvAnlage {
  id: string;
  type: 'pv_anlage';
  name?: string;
  description?: string;
  metadata?: Record<string, unknown>;
  maxPower: number;
  steuerboxId?: string;
  relayNumber?: number;

  // PV specific properties
  solarPanelAreaM2: number;
  efficiencyPercent: number;
  tiltAngleDegrees: number;
  azimuthDegrees: number;
  currentPowerGenerationKw?: number;
  lastCalculationTime?: string;
  installationDate?: string;
  manufacturer?: string;
  model?: string;
}

// Weather condition icons mapping
export const getWeatherIcon = (condition: string): string => {
  const lowerCondition = condition.toLowerCase();
  
  if (lowerCondition.includes('clear') || lowerCondition.includes('sunny')) {
    return '☀️';
  } else if (lowerCondition.includes('cloud')) {
    return '☁️';
  } else if (lowerCondition.includes('rain')) {
    return '🌧️';
  } else if (lowerCondition.includes('snow')) {
    return '❄️';
  } else if (lowerCondition.includes('storm') || lowerCondition.includes('thunder')) {
    return '⛈️';
  } else if (lowerCondition.includes('fog') || lowerCondition.includes('mist')) {
    return '🌫️';
  } else {
    return '🌤️'; // Default partly cloudy
  }
};

// PV efficiency color coding
export const getEfficiencyColor = (efficiency: number): string => {
  if (efficiency >= 80) return '#22c55e'; // Green - excellent
  if (efficiency >= 60) return '#84cc16'; // Light green - good
  if (efficiency >= 40) return '#eab308'; // Yellow - moderate
  if (efficiency >= 20) return '#f97316'; // Orange - low
  return '#ef4444'; // Red - very low
};

// Power generation status
export const getGenerationStatus = (currentKw: number, maxKw: number): {
  status: 'excellent' | 'good' | 'moderate' | 'low' | 'none';
  color: string;
  label: string;
} => {
  if (maxKw === 0) {
    return { status: 'none', color: '#6b7280', label: 'No Data' };
  }
  
  const efficiency = (currentKw / maxKw) * 100;
  
  if (efficiency >= 80) {
    return { status: 'excellent', color: '#22c55e', label: 'Excellent' };
  } else if (efficiency >= 60) {
    return { status: 'good', color: '#84cc16', label: 'Good' };
  } else if (efficiency >= 40) {
    return { status: 'moderate', color: '#eab308', label: 'Moderate' };
  } else if (efficiency >= 10) {
    return { status: 'low', color: '#f97316', label: 'Low' };
  } else {
    return { status: 'none', color: '#6b7280', label: 'Minimal' };
  }
};

// Format power values for display
export const formatPower = (powerKw: number): string => {
  if (powerKw >= 1000) {
    return `${fmt((powerKw / 1000), 1)} MW`;
  } else if (powerKw >= 1) {
    return `${fmt(powerKw, 2)} kW`;
  } else {
    return `${fmt((powerKw * 1000), 0)} W`;
  }
};

// Format efficiency percentage
export const formatEfficiency = (efficiency: number): string => {
  return `${fmt(efficiency, 1)}%`;
};

// Calculate estimated daily/monthly/yearly energy production
export const calculateEnergyProduction = (
  currentGenerationKw: number,
  timeframe: 'daily' | 'monthly' | 'yearly'
): number => {
  // Simplified calculation - in reality this would need more sophisticated modeling
  const hoursPerDay = 8; // Average effective sun hours
  const dailyKwh = currentGenerationKw * hoursPerDay;
  
  switch (timeframe) {
    case 'daily':
      return dailyKwh;
    case 'monthly':
      return dailyKwh * 30;
    case 'yearly':
      return dailyKwh * 365;
    default:
      return dailyKwh;
  }
};